"use client";

import Image from "next/image";
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
} from "framer-motion";
import { useRef } from "react";
import BlurFadeText from "@/components/common/blurFadeText";
import SectionTitle from "@/components/common/sectionTitle";
import Button from "@/components/common/button";

const EASE_OUT = [0.22, 1, 0.36, 1];

function revealProps(shouldReduceMotion, delay = 0, axis = "y", distance = 24) {
  if (shouldReduceMotion) {
    return {};
  }

  const hidden = { opacity: 0, filter: "blur(10px)" };

  if (axis === "x") {
    hidden.x = distance;
  } else if (axis === "-x") {
    hidden.x = -distance;
  } else {
    hidden.y = distance;
  }

  return {
    initial: hidden,
    whileInView: { opacity: 1, x: 0, y: 0, filter: "blur(0px)" },
    viewport: { once: true, amount: 0.22 },
    transition: { duration: 0.78, delay, ease: EASE_OUT },
  };
}

export default function PlatformOverviewExperience() {
  const shouldReduceMotion = useReducedMotion();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  const laptopX = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const phoneX = useTransform(scrollYProgress, [0, 1], [0, 180]);

  return (
    <section
      ref={heroRef}
      className="relative w-full overflow-visible sm:overflow-hidden rounded-lg"
    >
      {}
      <div className="relative z-10 mx-auto w-full max-w-328 xl:max-w-372 px-4 sm:px-6 lg:px-8 pt-16 sm:pt-32 lg:pt-42">
        <div className="grid items-center gap-10 lg:grid-cols-[1fr_1.5fr] lg:gap-36">
          {}
          <motion.div
            {...revealProps(shouldReduceMotion, 0.04, "y", 24)}
            className="order-2 lg:order-1"
          >
            <SectionTitle
              as="h1"
              className="mt-3 max-w-xl text-[2.6rem] leading-[0.88] tracking-[-0.06em] sm:text-[3.4rem] lg:text-[4.8rem]"
              text="Operational visibility in one platform."
            />
            <p className="mt-5 max-w-lg text-[0.95rem] leading-7 sm:text-base sm:leading-8">
              SML Connect brings together the day-to-day workflows your teams
              need with the reporting, accountability and visibility managers
              need to stay in control.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                text="Explore capabilities"
                variant="dark"
                href="#platform-capabilities"
                className="w-full justify-center sm:w-auto"
                onClick={(e) => {
                  e.preventDefault();
                  document
                    .getElementById("platform-capabilities")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
              />
              <Button
                text="Book a demo"
                variant="primary"
                href="#book-demo"
                className="w-full justify-center sm:w-auto"
                onClick={(e) => {
                  e.preventDefault();
                  document
                    .getElementById("book-demo")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
              />
            </div>
          </motion.div>

          {}
          <div className="order-1 lg:order-2">
            <div className="relative ml-0 lg:-ml-50 flex items-center justify-center">
              {}
              <motion.div
                style={shouldReduceMotion ? undefined : { x: laptopX }}
                className="relative w-full max-w-3xl mt-10"
              >
                <div className="relative aspect-4/3 w-full scale-100 sm:scale-120">
                  <Image
                    src="/images/other/sml-laptop-image.webp"
                    alt="SML Connect platform dashboard on laptop"
                    fill
                    className="object-contain object-center"
                    priority
                  />
                </div>
              </motion.div>

              {}
              <motion.div
                style={shouldReduceMotion ? undefined : { x: phoneX }}
                className="absolute bottom-0 -right-1 w-[46%] sm:absolute sm:-bottom-4 sm:-right-2 sm:w-[32%] lg:bottom-42 lg:-right-20"
              >
                <div className="relative aspect-3/4 w-full drop-shadow-2xl scale-100 sm:scale-125">
                  <Image
                    src="/images/other/sml-phone-image.webp"
                    alt="SML Connect mobile workflow on phone"
                    fill
                    className="object-contain object-center"
                    priority
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
